import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import Logo from "@/components/site/Logo";
import { useTheme } from "@/hooks/use-theme";
import ProfileMenu from "@/components/home/ProfileMenu";
import MobileTabBar from "@/components/home/MobileTabBar";
import { EXPLORE, BOOKSHELF, MY_WRITING } from "@/constants/testIds";

// Authenticated product navigation — deliberately just these three. Public
// marketing links (Discover anchor, Features, About, ...) live only in the
// logged-out Navbar; this header must never point back at landing anchors.
const APP_NAV = [
  { key: "explore", label: "Explore", to: "/app/explore", testid: EXPLORE.navLink },
  { key: "bookshelf", label: "Bookshelf", to: "/app/bookshelf", testid: BOOKSHELF.navLink },
  { key: "studio", label: "Studio", to: "/app/studio", testid: MY_WRITING.navLink },
];

function getActiveKey(pathname) {
  if (pathname.startsWith("/app/bookshelf")) return "bookshelf";
  if (pathname.startsWith("/app/studio")) return "studio";
  if (pathname.startsWith("/app/explore")) return "explore";
  return null;
}

export default function AppHeader() {
  const { dark, toggle } = useTheme();
  const { pathname } = useLocation();
  const activeKey = getActiveKey(pathname);

  return (
    <>
      <motion.header
        initial={{ y: -60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="sticky top-0 z-40 border-b border-line bg-cream/85 backdrop-blur-xl" data-testid="app-header"
      >
        <div className="u-container flex h-16 items-center justify-between">
          <Logo to="/app/explore" size="md" testId="app-logo" />

          <nav className="hidden items-center gap-1 md:flex" aria-label="Primary">
            {APP_NAV.map((item) => {
              const isActive = item.key === activeKey;
              return (
                <Link
                  key={item.key}
                  to={item.to}
                  data-testid={item.testid}
                  aria-current={isActive ? "page" : undefined}
                  className="group relative rounded-full px-4 py-2 text-sm font-medium transition-colors"
                >
                  <span className={isActive ? "font-semibold text-coral" : "text-ink/75 group-hover:text-coral"}>
                    {item.label}
                  </span>
                  {isActive && (
                    <motion.span
                      layoutId="app-nav-active-indicator"
                      transition={{ type: "spring", stiffness: 400, damping: 32 }}
                      className="absolute bottom-0.5 left-4 right-4 h-[2px] rounded-full bg-coral"
                    />
                  )}
                </Link>
              );
            })}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <ThemeToggle dark={dark} toggle={toggle} className="hidden sm:grid" />
            <ProfileMenu />
          </div>
        </div>
      </motion.header>

      <MobileTabBar activeKey={activeKey} />
    </>
  );
}
